import type { Price } from 'types/capi/shared';

import {
  type ContractStockStatus,
  type StockStatus,
  toContractStockStatus
} from '~/domain/stockStatus';

import InteractionEvent, {
  type InteractionTargetType
} from './InteractionEvent';

const NO_PRICE = -1;

export type NotificationPlacement =
  | 'banner'
  | 'content_block'
  | 'gallery'
  | 'product_card'
  | 'sticky'
  | 'wtb';

// What the button was at the moment it was clicked. The same control opens the modal to SET a
// first alert and to EDIT or cancel an existing one, and those are opposite intents — an
// alert_edit is not demand asked for, and one of them can end in the alert being removed. Only
// the client knows which was rendered, so it cannot be derived downstream.
export type NotificationElement =
  | 'alert_edit'
  | 'cancel_confirmed'
  | 'cancel_declined'
  | 'close'
  | 'confirm_email'
  | 'notify_me';

// The steps after the tap. The modal opens over the page, so these share the tap's view_id and
// carry the same product, price and placement; `step` says which screen a close or a "Cancel
// notifications?" answer came from. confirm_email replaces the tap for a shopper whose email is
// unconfirmed, on the same surface.
export type NotificationModalElement = Exclude<
  NotificationElement,
  'alert_edit' | 'notify_me'
>;

export type NotificationStep = 'sign_in' | 'size';

export type NotificationTargetProperties = {
  element: NotificationElement;
  module_position?: number;
  module_type?: string;
  placement: NotificationPlacement;
  position?: number;
  price_currency: string;
  price_regular: number;
  price_sale?: number;
  product_id: string;
  step?: NotificationStep;
  stock_status?: ContractStockStatus;
};

type EventData = {
  element?: NotificationModalElement;
  gesture?: string;
  guestId?: string;
  isBot: boolean;
  isNotified: boolean;
  modulePosition?: number;
  moduleType?: string;
  placement: NotificationPlacement;
  position?: number;
  price: Price;
  productId: number;
  step?: NotificationStep;
  stockStatus?: StockStatus;
};

class NotificationInteractionEvent extends InteractionEvent<
  EventData,
  NotificationTargetProperties
> {
  protected get targetType(): InteractionTargetType {
    return 'notification';
  }

  protected getTargetProperties(): NotificationTargetProperties {
    return {
      element: this.element(),
      module_position: this.data.modulePosition,
      module_type: this.data.moduleType,
      placement: this.data.placement,
      position: this.data.position,
      price_currency: this.data.price.currency,
      price_regular: this.data.price.regular ?? NO_PRICE,
      price_sale: this.data.price.sale ?? undefined,
      product_id: String(this.data.productId),
      step: this.data.step,
      stock_status: this.stockStatus()
    };
  }

  private element(): NotificationElement {
    if (this.data.element) return this.data.element;

    return this.data.isNotified ? 'alert_edit' : 'notify_me';
  }

  // Omitted rather than defaulted when the client doesn't know. TBC is a real stock status, so
  // defaulting to it would make "we couldn't map this" indistinguishable from "the retailer
  // hasn't said" — the error the design forbids one field over, where a missing module_type maps
  // to unknown and never to a real module.
  private stockStatus(): ContractStockStatus | undefined {
    const status = this.data.stockStatus;

    if (!status) return undefined;

    return toContractStockStatus(status);
  }
}

export default NotificationInteractionEvent;
